/* ==========================================================================
   Clapstore — Bestsellers, tabbed by age.

   This file does exactly two things, because everything else the section
   needs already exists:

     1. Switches the age tabs. Every panel is already in the DOM, so a switch
        is an attribute flip — no fetch, no spinner, no layout thrash. The
        catalogue is ~30 products; a Section Rendering round trip per tap would
        cost more than the whole rendered markup does.
     2. Drives the desktop arrow buttons and keeps them honest about whether
        there is anything left to scroll.

   Drag-to-scroll is NOT here. The rails carry .cs-rail, and cs-smooth.js
   already wires pointer drag, touch swipe and snap onto that class. A second
   implementation on the same element is how two systems end up writing
   conflicting scroll positions on the same frame.

   Contract with sections/cs-bestsellers.liquid and assets/cs-bestsellers.css
   --------------------------------------------------------------------------
   container  [data-cs-best]
   tab row    [data-cs-tablist]
   tab        [data-cs-tab]  role=tab  aria-controls  aria-selected  tabindex
              data-cs-block-id           theme-editor block selection
              data-cs-collection-url     feeds the see-everything link
   panel      [data-cs-panel]  role=tabpanel  [hidden] when inactive
   rail       [data-cs-rail]   .cs-rail
   arrows     [data-cs-prev]  [data-cs-next]
   link       [data-cs-explore]  [data-cs-explore-fixed] if merchant-set

   Performance rules this file obeys
   --------------------------------------------------------------------------
   * The scroll listener is passive and coalesced into one rAF, so a flick
     never queues more than a frame of work.
   * Geometry is read once per frame inside that rAF, never during the event.
   * A hidden panel measures zero, so arrow state is recomputed when a panel is
     revealed rather than trusted from load time.
   * It no-ops entirely when the section is absent, and re-initialises on
     shopify:section:load.
   ========================================================================== */
(function () {
  'use strict';

  var ROOT = '[data-cs-best]';

  /* Slack left when scrolling a tab back into view, so the tab does not land
     flush against the edge it just came from. One step on the spacing scale. */
  var EDGE = 16;

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var instances = [];

  /* ---------------------------------------------------------------------
     One rail: the arrows, their disabled state, and one page of travel.
     --------------------------------------------------------------------- */
  function makeRail(panel) {
    var rail = panel.querySelector('[data-cs-rail]');
    if (!rail) return null;

    var prev = panel.querySelector('[data-cs-prev]');
    var next = panel.querySelector('[data-cs-next]');
    var rtl = window.getComputedStyle(rail).direction === 'rtl' ? -1 : 1;
    var frame = 0;

    /* One page = as many whole cards as currently fit. Scrolling by the
       viewport width instead would leave a card sliced down the middle and
       let snapping argue with the animation on the way back. */
    function page() {
      var card = rail.firstElementChild;
      if (!card) return 0;

      var gap = parseFloat(window.getComputedStyle(rail).columnGap) || 0;
      var step = card.getBoundingClientRect().width + gap;
      if (!(step > 0)) return 0;

      var fits = Math.floor((rail.clientWidth + gap) / step);
      return step * Math.max(1, fits);
    }

    function sync() {
      frame = 0;

      var max = rail.scrollWidth - rail.clientWidth;
      var pos = Math.abs(rail.scrollLeft);
      var idle = max <= 1;   // nothing to scroll, or the panel is hidden

      if (prev) {
        prev.hidden = idle;
        prev.disabled = idle || pos <= 1;
      }
      if (next) {
        next.hidden = idle;
        next.disabled = idle || pos >= max - 1;
      }
    }

    function schedule() {
      if (!frame) frame = window.requestAnimationFrame(sync);
    }

    function move(sign) {
      var amount = page();
      if (!amount) return;
      rail.scrollBy({
        left: sign * rtl * amount,
        behavior: reduced ? 'auto' : 'smooth'
      });
    }

    rail.addEventListener('scroll', schedule, { passive: true });
    if (prev) prev.addEventListener('click', function () { move(-1); });
    if (next) next.addEventListener('click', function () { move(1); });

    /* A hidden panel has no size. The observer is what tells us it just got
       one, which is the moment the arrows become meaningful. */
    var ro = null;
    if ('ResizeObserver' in window) {
      ro = new ResizeObserver(schedule);
      ro.observe(rail);
    } else {
      window.addEventListener('resize', schedule, { passive: true });
    }

    sync();

    return {
      sync: sync,
      schedule: schedule,
      destroy: function () { if (ro) ro.disconnect(); }
    };
  }

  /* ---------------------------------------------------------------------
     One section: the tablist.
     --------------------------------------------------------------------- */
  function makeSection(root) {
    var list = root.querySelector('[data-cs-tablist]');
    var tabs = Array.prototype.slice.call(root.querySelectorAll('[data-cs-tab]'));

    var explore = root.querySelector('[data-cs-explore]');
    var exploreFollows = !!explore && !explore.hasAttribute('data-cs-explore-fixed');

    var current = 0;

    function panelFor(tab) {
      return document.getElementById(tab.getAttribute('aria-controls'));
    }

    /* Rails are keyed off the tabs rather than off document order, so the two
       lists cannot drift apart if the markup is ever reordered. */
    var rails = tabs.map(function (tab) {
      var panel = panelFor(tab);
      return panel ? makeRail(panel) : null;
    });

    /* Keep the selected tab visible when the row scrolls on a phone. Written
       against the row's own scrollLeft rather than scrollIntoView, which is
       free to scroll the whole page vertically to satisfy the same request. */
    function reveal(tab) {
      if (!list) return;
      var row = list.getBoundingClientRect();
      var box = tab.getBoundingClientRect();

      if (box.left < row.left) {
        list.scrollLeft -= (row.left - box.left) + EDGE;
      } else if (box.right > row.right) {
        list.scrollLeft += (box.right - row.right) + EDGE;
      }
    }

    /* ------------------------------------------------------------------
       The orange rule slides between tabs instead of each tab growing its
       own. One continuous element travelling reads as a response to the
       tap; two independent scaleX transitions read as two unrelated things
       happening at once, which is what made the row feel static.

       Driven by transform only — translateX plus scaleX against a
       full-width bar — so it never touches layout. All the reads happen
       together, before any write, so this cannot force a reflow mid-tap.
       ------------------------------------------------------------------ */
    var rule = root.querySelector('[data-cs-tabrule]');
    /* Only stand the per-tab underlines down once we know the rule is really
       here and drivable — with no JS, the CSS-only underlines must survive. */
    if (rule && list) list.classList.add('has-rule');

    function moveRule(tab) {
      if (!rule || !list || !tab) return;
      var listBox = list.getBoundingClientRect();
      var tabBox = tab.getBoundingClientRect();
      var width = listBox.width || 1;
      var offset = (tabBox.left - listBox.left) + list.scrollLeft;

      rule.style.transform =
        'translateX(' + offset + 'px) scaleX(' + (tabBox.width / width) + ')';
      rule.style.opacity = '1';
    }

    function select(index, focus) {
      if (index < 0 || index >= tabs.length) return;
      current = index;

      tabs.forEach(function (tab, i) {
        var on = i === index;
        var panel = panelFor(tab);

        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        tab.tabIndex = on ? 0 : -1;

        /* `hidden` rather than a class: it takes the panel out of the
           accessibility tree and out of the tab order, not just out of view. */
        if (panel) {
          if (on) {
            panel.removeAttribute('hidden');
            /* Retrigger the enter animation. Reading offsetWidth between the
               remove and the re-add is what forces the restart. */
            panel.classList.remove('is-entered');
            void panel.offsetWidth;
            panel.classList.add('is-entered');
          } else {
            panel.setAttribute('hidden', '');
            panel.classList.remove('is-entered');
          }
        }
      });

      moveRule(tabs[index]);

      if (explore && exploreFollows) {
        var url = tabs[index].getAttribute('data-cs-collection-url');
        if (url) {
          explore.setAttribute('href', url);
          explore.hidden = false;
        } else {
          explore.hidden = true;
        }
      }

      if (focus) {
        tabs[index].focus();
        reveal(tabs[index]);
      }

      /* The panel was zero-sized a moment ago. Measure it after the browser
         has had a chance to lay it out. */
      var r = rails[index];
      if (r) window.requestAnimationFrame(r.sync);
    }

    if (list) {
      list.addEventListener('scroll', function () {
        if (tabs[current]) moveRule(tabs[current]);
      }, { passive: true });
    }
    if (typeof ResizeObserver === 'function' && list) {
      new ResizeObserver(function () {
        if (tabs[current]) moveRule(tabs[current]);
      }).observe(list);
    }

    tabs.forEach(function (tab, i) {
      tab.addEventListener('click', function () { select(i, false); });
    });

    if (list) {
      list.addEventListener('keydown', function (e) {
        var last = tabs.length - 1;
        var target;

        if (e.key === 'ArrowRight') target = current === last ? 0 : current + 1;
        else if (e.key === 'ArrowLeft') target = current === 0 ? last : current - 1;
        else if (e.key === 'Home') target = 0;
        else if (e.key === 'End') target = last;
        else return;

        e.preventDefault();
        select(target, true);
      });
    }

    return {
      root: root,
      tabs: tabs,
      select: select,
      destroy: function () {
        rails.forEach(function (r) { if (r) r.destroy(); });
      }
    };
  }

  /* ---------------------------------------------------------------------
     Boot
     --------------------------------------------------------------------- */
  function init(scope) {
    var roots = (scope || document).querySelectorAll(ROOT);
    if (!roots.length) return;                 // no section on this page: do nothing

    Array.prototype.forEach.call(roots, function (root) {
      if (root.dataset.csBestReady === '1') return;
      root.dataset.csBestReady = '1';
      instances.push(makeSection(root));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); }, { once: true });
  } else {
    init();
  }

  // Theme editor: sections are re-rendered in place.
  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    instances = instances.filter(function (inst) {
      if (!e.target.contains(inst.root)) return true;
      inst.destroy();
      return false;
    });
  });

  // Selecting an age block in the editor should show that age.
  document.addEventListener('shopify:block:select', function (e) {
    instances.forEach(function (inst) {
      if (!inst.root.contains(e.target)) return;
      inst.tabs.forEach(function (tab, i) {
        if (tab.getAttribute('data-cs-block-id') === e.detail.blockId) inst.select(i, false);
      });
    });
  });
})();
